import { ADVANCED_ORDER_TYPE_LABELS, isTriggerOrderType } from '@/constants/advancedOrder';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import { XIcon } from '@/ui/Icons/XIcon';

interface LimitOrderDismissibleWarningProps {
  onDismiss: () => void;
}

export function LimitOrderDismissibleWarning({ onDismiss }: LimitOrderDismissibleWarningProps) {
  const [orderType] = useAdvancedOrderType();
  const orderTypeLabel = ADVANCED_ORDER_TYPE_LABELS[orderType];
  const priceTerm = isTriggerOrderType(orderType) ? 'trigger' : 'limit';

  return (
    <div
      className={fontStyles({
        variant: 'BODY_SM',
        class: [
          'bg-special-background-card',
          'py-5',
          'pl-6',
          'pr-3',
          'relative',
          'rounded-2xl',
          'overflow-hidden',
          'flex',
          'justify-between',
          'items-center',
          'gap-2',
          'mt-3',
          'before:content-none',
          'after:content-none',
        ],
      })}
    >
      <div
        className="p-1.5 rounded-full inline-flex text-primitive-basic-white h-fit w-fit mr-1 shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08),0px_1px_2px_0px_rgba(0,0,0,0.16)]"
        style={{
          background: 'linear-gradient(315deg, #9299A6 0%, rgba(146, 153, 166, 0.80) 100%)',
        }}
      >
        <InfoCircleSolidSmallIcon />
      </div>
      <p>
        The {orderTypeLabel} order may not fill exactly when onchain prices reach the {priceTerm}{' '}
        price.{' '}
        <a
          className={linkStyles({
            color: 'purple',
            underline: true,
            font: 'BODY_SM',
            class: 'text-350 leading-4.5',
          })}
          href="https://help.matcha.xyz/en/articles/4209180-why-hasn-t-my-limit-order-been-filled"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>
      </p>

      <button
        aria-label="Dismiss warning"
        className={buttons({
          size: 'small',
          color: 'transparent',
          iconOnly: true,
          shape: 'rounded',
          class: ['self-start', 'h-fit', 'w-fit', '-mt-2', 'p-2.5'],
        })}
        onClick={onDismiss}
      >
        <XIcon aria-hidden className="h-4 w-4" />
      </button>

      {/* STYLING HELPERS */}
      <div className="opacity-14 absolute top-0 left-0 bottom-0 right-0 rounded-2xl overflow-hidden pointer-events-none before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:bottom-0 before:shadow-[0px_4px_34px_0px_var(--token-color-speical-background-card)_inset,0px_4px_34px_0px_var(--token-color-special-background-card)_inset] before:z-1 before:rounded-2xl">
        <div
          className="w-[324px] h-[159px] top-[-54px] left-[-130px] absolute"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, #747681 0%, rgba(123, 127, 148, 0.00) 100%)',
          }}
        />
        <div
          className="w-[357px] h-[152px] top-[-73px] left-[-40px] bottom-[-42px] transform rotate-[-21.75deg] opacity-30 absolute"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, #6D6A81 0%, rgba(102, 100, 120, 0.00) 100%)',
          }}
        />
      </div>
    </div>
  );
}
